import cloneDeep from 'lodash/cloneDeep';
import { fromJS } from 'immutable';
import { isImmutable } from 'pluginServices/helpers/immutableHelpers';

/**
 * This function is used to modify the data object (when a key is deleted) which is used as part of
 * interactive / component config replace. If value is present, stamp it to the key else delete the key itself
 * @func modifyDataObject
 * @param {String} propertyKey - schema attribute key
 * @param {Object} data - the config data object
 * @param {String} value - value that will be added for the key
 * @param {Number} groupEntriesIndex - index of the groupEntries
 * 
 * @return {Object} - updated copy of data object
 */
export const modifyDataObject = (propertyKey, data, value, groupEntriesIndex) => {
  const schema = cloneDeep(data);
  if (schema && !Array.isArray(schema)) {
    Object.keys(schema).forEach((key) => {
      if (key === propertyKey) {
        if (value !== null && value !== undefined) {
          schema[key] = value;
        } else {
          delete schema[key];
        }
      } else if (typeof schema[key] === 'object') {
        if(groupEntriesIndex >= 0 && Array.isArray(schema[key])) {
          const groupEntries = schema[key].map((item, index) => {
            if(value && groupEntriesIndex === index) {
              item[propertyKey] = value;
            } else if(groupEntriesIndex === index){
              item[propertyKey] = '';
            }
            return item;
          });
          schema[key] = groupEntries;
        } else {
          schema[key] = modifyDataObject(propertyKey, schema[key], value);
        }
      }
    });
  }
  return schema;
};

/**
 * This function is used to check the validity of the value binded object in data
 * If current value binding is invalid then a key `isBindingInvalid` is added to the object
 * @func handleValueBinderValidity
 * @param {Object} dataObj - the config data object
 * @param {Object} dataCheck - the current lesson scope list
 * @return {Object} - updated data
 */
export const handleValueBinderValidity = (dataObj, dataCheck) => {
  const obj = cloneDeep(dataObj);
  Object.entries(obj).forEach(([key, value]) => {
    if (typeof (value) === 'object' && value.constructor === Object) {
      obj[key] = handleValueBinderValidity(value, dataCheck);
    } else if (key === 'sourceId' && dataCheck && !dataCheck[obj.sourceId]) {
      obj.isBindingInvalid = true;
    }
  });
  return obj;
};

/**
 * This function is used to create value binded object
 * @func createValueBinderDataObject
 * @param {Object} propertyPath
 * @param {Object} valueBinder
 * @param {Object} listGroup 
 * @param {String} selectedLabelProperty *
 * @return {Object} - updated data
 */
export const createValueBinderDataObject = (propertyPath, valueBinder, listGroup = [], selectedLabelProperty) => {
  let valueObject = {};
  let nestedObject = {};
  for (let i = propertyPath.length - 1; i >= 0; i--) {
    if (Object.keys(valueObject).length === 0) {
      if(listGroup.length) {
        listGroup[propertyPath[i]][selectedLabelProperty] = valueBinder;
      }
      valueObject[propertyPath[i]] = valueBinder;
    } else {
      nestedObject = {};
      nestedObject[propertyPath[i]] = listGroup.length ? { ...listGroup } : { ...valueObject };
      valueObject = { ...nestedObject };
    }
  }
  return valueObject;
};


/**
 * This fuction finds all bound reference paths of a particular property nested inside a config object
 * @param  {Object} nestedPropertyKey
 * @param  {Object} nestedPropertyValue
 * @param  {Object} inputConfig :: config object
 * @returns {Array<Array<string>>} Array of paths. Each path is an array of strings.
 */
export const getBoundReferencePathsOfNestedProperty = (nestedPropertyKey, nestedPropertyValue, inputConfig) => {
  const configImmutable = isImmutable(inputConfig) ? inputConfig : fromJS(inputConfig);

  const localPaths = getBoundReferencePaths(nestedPropertyValue);
  const nestedReferencePaths = localPaths.map((path) => {
    const flattenedPath = path.flat();
    const nestedPathExists = configImmutable.getIn([nestedPropertyKey, ...[flattenedPath]], 'schemaProperty');
    if (nestedPathExists) {
      return ([nestedPropertyKey, ...flattenedPath]);
    }
    return [];
  });

  return nestedReferencePaths;
};

/**
 * This fuction finds all bound references within a config and returns the path of said references
 * @param  {Object} inputConfig :: config object
 * @returns {Array<Array<string>>} Array of paths. Each path is an array of strings.
 */
export const getBoundReferencePaths = (inputConfig) => {
  if (!inputConfig) return [];
  const config = isImmutable(inputConfig) ? inputConfig.toJS() : { ...inputConfig };

  const paths = Object.keys(config).reduce((total, key) => {
    if (config[key] && typeof config[key] === 'object' &&
      config[key].schemaProperty && !config[key].noResolve) {
      total.push([key]);
    } else if (config[key] && typeof config[key] === 'object') {
      const nestedPaths = getBoundReferencePaths(config[key]);
      const newArr = nestedPaths.map((path) => [key, ...path]);
      total.push(...newArr);
    }
    return total;
  }, []);
  return paths;
};